import { SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import {
  ServiceMasterListItemModel,
  SiteMasterListItemModel,
} from '@customer-portal/data-access/global';
import { SharedSelectMultipleDatum } from '@customer-portal/shared/components/select/multiple-mod';
import {
  COLUMN_DELIMITER,
  StatesClasses,
} from '@customer-portal/shared/constants';
import {
  convertToUtcDate,
  mapApiResponseToPageName,
} from '@customer-portal/shared/helpers';
import {
  CustomTreeNode,
  ServiceDetailsMaster,
  SiteDetailsMaster,
} from '@customer-portal/shared/models';

import { feedback, iconMap } from '../../constants';
import { ActionsItemsDto } from '../../dtos';
import {
  ActionSiteModel,
  ActionsListItemEnrichedDto,
  ActionsModel,
} from '../../models';

export class ActionsListMapperService {
  static mapToActionsListEnrichedModel(
    items: ActionsItemsDto[],
    domSanitizer: DomSanitizer,
    siteMasterList: SiteMasterListItemModel[],
    serviceMasterList: ServiceMasterListItemModel[],
  ): ActionsListItemEnrichedDto[] {
    if (!items) {
      return [];
    }

    const serviceMap = new Map(serviceMasterList.map((s) => [s.id, s]));
    const siteMap = new Map(siteMasterList.map((s) => [s.id, s]));
    const companyMap = new Map(siteMasterList.map((s) => [s.companyId, s]));

    const result = items.map((actions: ActionsItemsDto) => {
      const service = Array.from(
        new Set(
          (actions.serviceIds || [])
            .map((serviceId) => serviceMap.get(serviceId)?.serviceName)
            .filter(Boolean),
        ),
      ).join(COLUMN_DELIMITER);

      const matchedSites = (actions.siteIds || [])
        .map((siteId) => siteMap.get(siteId))
        .filter(Boolean);

      const companyName = companyMap.get(actions.accountId)?.companyName ?? '';

      const site = Array.from(
        new Set(matchedSites?.map((s) => s?.siteName)?.filter(Boolean)),
      )?.join(COLUMN_DELIMITER);

      const siteDetails = this.mapSiteDetails(
        actions.siteIds,
        actions.accountId,
        siteMap,
      );
      const serviceDetails = this.mapServiceDetails(
        actions.serviceIds,
        serviceMap,
      );

      return {
        id: actions.id,
        actionName: actions.subject,
        dueDate: convertToUtcDate(actions.dueDate),
        message:
          domSanitizer.sanitize(SecurityContext.HTML, actions.message) ?? '',
        service,
        site,
        entityType: actions.entityType,
        entityId: actions.entityId,
        highPriority: actions.highPriority,
        snowLink: actions.snowLink,
        companyName,
        siteDetails,
        serviceDetails,
        companyId: actions.accountId,
        category: actions.entityType,
        categoryTagState: this.getCategoryTagState(actions.highPriority),
      };
    });

    return result;
  }

  static recalculateState(
    actions: ActionsListItemEnrichedDto[],
    filterCompanies: number[],
    filterServices: number[],
    filterSites: number[],
    filterCategories: string[],
    isHighPriority: boolean,
  ) {
    const filterCompaniesSet = new Set(filterCompanies);
    const filterServicesSet = new Set(filterServices);
    const filterSitesSet = new Set(filterSites);
    const filterCategoriesSet = new Set(filterCategories);

    const filtered = actions.filter((item) => {
      const matchesCompany =
        !filterCompaniesSet.size || filterCompaniesSet.has(item.companyId);
      const matchesService =
        !filterServicesSet.size ||
        item.serviceDetails.some((s) => filterServicesSet.has(s.id));
      const matchesSite =
        !filterSitesSet.size ||
        item.siteDetails.some((s) => filterSitesSet.has(s.id));
      const matchesCategory =
        !filterCategoriesSet.size || filterCategoriesSet.has(item.category);
      const matchesHighPriority = !isHighPriority || item.highPriority;

      return (
        matchesCompany &&
        matchesService &&
        matchesSite &&
        matchesCategory &&
        matchesHighPriority
      );
    });

    const sortedByDueDateFilteredActions =
      filtered.length > 0
        ? [...filtered].sort((a, b) => {
            const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
            const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;

            return dateB - dateA;
          })
        : filtered;

    const dataCompanies = this.buildDataCompanies(filtered);
    const dataServices = this.buildDataServices(filtered);
    const sitesDto = this.buildActionsFilterSitesData(filtered);
    const dataCategories = this.buildDataCategories(filtered);
    const sortedSitesDto = [...sitesDto].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    const dataSites = this.mapToFilterSites(sortedSitesDto);

    return {
      actionItems: this.mapToActionsListModel(sortedByDueDateFilteredActions),
      dataCompanies,
      dataServices,
      dataSites,
      dataCategories,
    };
  }

  private static mapToActionsListModel(
    actionItems: ActionsListItemEnrichedDto[],
  ): ActionsModel[] {
    return actionItems.map((item) => ({
      id: item.id,
      actionName: item.actionName,
      category: item.category,
      categoryTagState: item.categoryTagState,
      dueDate: item.dueDate,
      highPriority: item.highPriority,
      message: item.message,
      service: item.service,
      site: item.site,
      entityType:
        item.entityType === feedback
          ? feedback
          : mapApiResponseToPageName(item.entityType),
      entityId: item.entityId,
      snowLink: item.snowLink,
      actions: [
        {
          actionType: 'redirect',
          iconClass: 'pi-angle-right',
          label: 'angle-right',
          url: '',
        },
      ],
      dateWithIcon: {
        displayIcon: item.highPriority,
        iconClass: 'pi pi-info-circle',
        tooltipMessage: '',
        iconPosition: 'prefix',
      },
      iconTooltip: {
        displayIcon: true,
        iconClass: this.getActionIconClass(
          item.snowLink && item.entityType !== feedback
            ? 'snowLink'
            : item.entityType?.toLowerCase(),
        ),
        tooltipMessage: '',
        iconPosition: 'prefix',
      },
    }));
  }

  private static mapSiteDetails(
    siteIds: number[],
    companyId: number,
    siteMap: Map<number, SiteMasterListItemModel>,
  ): SiteDetailsMaster[] {
    return (siteIds || [])
      .map((siteId: number) => {
        const site = siteMap.get(siteId);

        if (site?.companyId !== companyId) {
          return undefined;
        }

        const { id, siteName, siteNickname, city, countryId, countryName } =
          site;

        return {
          id,
          siteName,
          siteNickname,
          city,
          countryId,
          countryName,
          companyId,
        };
      })
      .filter((site): site is SiteDetailsMaster => site !== undefined);
  }

  private static mapServiceDetails(
    serviceIds: number[],
    serviceMap: Map<number, ServiceMasterListItemModel>,
  ): ServiceDetailsMaster[] {
    return (serviceIds || [])
      .map((serviceId: number) => {
        const service = serviceMap.get(serviceId);

        return service
          ? {
              id: service.id,
              serviceName: service.serviceName,
            }
          : undefined;
      })
      .filter(
        (service): service is ServiceDetailsMaster => service !== undefined,
      );
  }

  private static mapToFilterSites(
    data: ActionSiteModel[] | undefined,
    depth = 0,
    parentIds: Set<number | string> = new Set(),
  ): CustomTreeNode[] {
    return (data || []).map((datum) => {
      if (parentIds.has(datum.id)) {
        return {
          data: datum.id,
          depth,
          key: `${datum.id}-${datum.label}`,
          label: datum.label,
          siteNickname: datum.siteNickname,
          children: [],
        };
      }
      const nextParentIds = new Set(parentIds);
      nextParentIds.add(datum.id);

      return {
        data: datum.id,
        depth,
        key: `${datum.id}-${datum.label}`,
        label: datum.label,
        siteNickname: datum.siteNickname,
        children: Array.isArray(datum.children)
          ? this.mapToFilterSites(datum.children, depth + 1, nextParentIds)
          : [],
      };
    });
  }

  private static buildDataCategories(
    enrichedActionItems: ActionsListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<string>[] {
    return Array.from(
      new Set(
        enrichedActionItems
          .map((item) => item.category)
          .filter((cat): cat is string => !!cat),
      ),
    )
      .map((category) => ({
        label: category,
        value: category,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildDataCompanies(
    enrichedActionItems: ActionsListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<number>[] {
    const companyMap = new Map<number, string>();
    enrichedActionItems.forEach((item) => {
      if (item.companyId && item.companyName) {
        companyMap.set(item.companyId, item.companyName);
      }
    });

    return Array.from(companyMap.entries())
      .map(([id, name]) => ({
        label: name,
        value: id,
      }))
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildDataServices(
    enrichedActionItems: ActionsListItemEnrichedDto[],
  ): SharedSelectMultipleDatum<number>[] {
    const serviceMap = new Map<number, string>();
    enrichedActionItems.forEach((item) => {
      (item.serviceDetails || []).forEach((service) => {
        if (service?.id && service.serviceName) {
          serviceMap.set(service.id, service.serviceName);
        }
      });
    });

    return Array.from(serviceMap.entries())
      .map(([id, name]) => ({
        label: name,
        value: id,
      }))
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private static buildActionsFilterSitesData(
    enrichedActionItems: ActionsListItemEnrichedDto[],
  ): ActionSiteModel[] {
    const countryMap = new Map<string, Map<string, SiteDetailsMaster[]>>();
    let cityMap = new Map<string, SiteDetailsMaster[]>();

    enrichedActionItems.forEach((item) => {
      (item.siteDetails || []).forEach((site) => {
        if (!site) {
          return;
        }

        if (!site.countryName || !site.city || !site.siteName) {
          return;
        }

        if (!countryMap.has(site.countryName)) {
          countryMap.set(site.countryName, new Map());
        }
        cityMap = countryMap.get(site.countryName)!;

        if (!cityMap.has(site.city)) {
          cityMap.set(site.city, []);
        }

        if (!(cityMap.get(site.city) ?? []).some((s) => s.id === site.id)) {
          cityMap.get(site.city)?.push(site);
        }
      });
    });

    let cityIdCounter = 1000;
    const countryNodes: ActionSiteModel[] = [];

    const sortedCountryEntries = Array.from(countryMap.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    );
    sortedCountryEntries.forEach(([countryName, cityMapEntry]) => {
      const firstCitySites = Array.from(cityMapEntry.values())[0];
      const countryId =
        firstCitySites?.[0]?.countryId || Math.floor(Math.random() * 100000);

      const sortedCityEntries = Array.from(cityMapEntry.entries()).sort(
        (a, b) => a[0].localeCompare(b[0]),
      );
      const cityNodes: ActionSiteModel[] = [];
      sortedCityEntries.forEach(([cityName, sites]) => {
        const cityId = cityIdCounter;
        cityIdCounter += 1;
        const sortedSites = [...sites].sort((a, b) =>
          a.siteName.localeCompare(b.siteName),
        );
        const siteNodes: ActionSiteModel[] = sortedSites
          .map((site) => ({
            id: site.id,
            label: site.siteName,
            siteNickname: site.siteNickname,
          }))
          .filter(Boolean);

        cityNodes.push({
          id: `city_${cityId}`,
          label: cityName,
          children: siteNodes,
        });
      });

      countryNodes.push({
        id: countryId,
        label: countryName,
        children: cityNodes,
      });
    });

    return countryNodes.filter(Boolean);
  }

  private static getActionIconClass(entityType: string): string {
    const matchedKey = this.findMatchingKey(entityType);

    return matchedKey ? iconMap[matchedKey] : iconMap['default'];
  }

  private static findMatchingKey(entityType: string): string | undefined {
    return Object.keys(iconMap).find((key) => entityType.includes(key));
  }

  private static getCategoryTagState(
    highPriority: boolean,
  ): string | undefined {
    return highPriority ? StatesClasses.MistyRose : undefined;
  }
}
